import React, { useMemo, useState } from "react";
import Select from "react-select";

/**
 * Type-to-search replacement for the native <select> used in the filter
 * panels. Styled to match `.wf-input-wrap .form-control` in App.css so it
 * sits alongside the plain inputs without looking like a different control.
 *
 * Values in/out are plain strings, matching what the native select gave the
 * callers, so state handlers do not need to change.
 */
const SearchableSelect = ({
  id,
  name,
  value,
  options = [],
  placeholder = "Select...",
  onChange,
  icon,
  isDisabled = false,
  isClearable = true,
  // "sm" matches the compact inline control bars used on the report screens;
  // the default matches the full-width filter panels.
  size = "md",
  width,
}) => {
  // Mirrors `.wf-input-wrap:focus-within .wf-input-icon`, which cannot apply
  // here because the wrapper is a plain div (see the render note below).
  const [isFocused, setIsFocused] = useState(false);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value],
  );

  const styles = useMemo(() => {
    const padLeft = icon ? 27 : 0;
    const isSm = size === "sm";
    const fontSize = isSm ? "12.5px" : "13px";
    return {
      control: (base, state) => ({
        ...base,
        minHeight: isSm ? "35px" : "38px",
        borderRadius: isSm ? "8px" : "9px",
        borderWidth: isSm ? "1px" : "1.5px",
        borderColor: state.isFocused ? "#2563eb" : "#cbd5e1",
        backgroundColor: state.isDisabled ? "#f8fafc" : state.isFocused ? "#fafbff" : "#ffffff",
        boxShadow: state.isFocused
          ? "0 0 0 4px rgba(37, 99, 235, 0.1), 0 2px 6px -1px rgba(37, 99, 235, 0.08)"
          : "0 1px 3px 0 rgba(0, 0, 0, 0.03)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          borderColor: state.isFocused ? "#2563eb" : "#94a3b8",
        },
      }),
      valueContainer: (base) => ({
        ...base,
        padding: `2px 6px 2px ${padLeft + 6}px`,
      }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
        fontSize,
      }),
      singleValue: (base) => ({
        ...base,
        color: "#1e293b",
        fontSize,
      }),
      input: (base) => ({
        ...base,
        color: "#1e293b",
        fontSize,
        margin: 0,
        padding: 0,
      }),
      indicatorSeparator: () => ({ display: "none" }),
      indicatorsContainer: (base) => ({
        ...base,
        gap: "2px",
        paddingRight: "4px",
      }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: "4px",
        color: "#94a3b8",
        "& svg": { width: "16px", height: "16px" },
        "&:hover": { color: "#64748b" },
      }),
      // Sized down from react-select's 20px default and given its own hover
      // target, so it reads as a quiet affordance rather than a second chevron.
      clearIndicator: (base) => ({
        ...base,
        padding: "3px",
        borderRadius: "6px",
        color: "#cbd5e1",
        cursor: "pointer",
        transition: "color 0.15s ease, background-color 0.15s ease",
        "& svg": { width: "13px", height: "13px" },
        "&:hover": {
          color: "#dc2626",
          backgroundColor: "rgba(220, 38, 38, 0.08)",
        },
      }),
      // Portalled to body so the menu is not clipped by the filter panel's
      // overflow; the zIndex clears bootstrap modals.
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({
        ...base,
        borderRadius: "10px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 24px -8px rgba(15, 23, 42, 0.18)",
        overflow: "hidden",
        zIndex: 9999,
      }),
      menuList: (base) => ({ ...base, padding: "4px" }),
      option: (base, state) => ({
        ...base,
        fontSize,
        borderRadius: "7px",
        padding: "8px 10px",
        cursor: "pointer",
        color: state.isSelected ? "#1d4ed8" : "#334155",
        fontWeight: state.isSelected ? 700 : 500,
        backgroundColor: state.isSelected
          ? "#eff6ff"
          : state.isFocused
            ? "#f1f5f9"
            : "transparent",
        "&:active": { backgroundColor: "#e2e8f0" },
      }),
      noOptionsMessage: (base) => ({ ...base, fontSize, color: "#94a3b8" }),
    };
  }, [icon, size]);

  // A plain div rather than `.wf-input-wrap`: that class forces
  // width:100%!important, which an inline control bar cannot override. The
  // icon is therefore positioned here instead of by App.css.
  return (
    <div style={{ width: width || "100%", position: "relative" }}>
      {icon && (
        <i
          className={`fa ${icon}`}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: isFocused ? "#2563eb" : "#94a3b8",
            fontSize: size === "sm" ? "12px" : "13px",
            pointerEvents: "none",
            transition: "color 0.2s ease",
            zIndex: 2,
          }}
        />
      )}
      <Select
        inputId={id}
        name={name}
        options={options}
        value={selected}
        onChange={(opt) => onChange(opt ? opt.value : "")}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isClearable={isClearable}
        isSearchable
        styles={styles}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPlacement="auto"
        noOptionsMessage={() => "No matches found"}
      />
    </div>
  );
};

export default SearchableSelect;
